import type { ConsentSnapshot, ConsentStorage, Result } from './types';
import { createLocalStorageAdapter } from './storage';
import type { RetryQueueItem } from './storage';

const MAX_RETRY_ATTEMPTS = 5;

export interface ApiClientOptions {
  apiBase: string;
  brand: 'kiedynabory' | 'hotel-jawor';
  storage: ConsentStorage;
  fetchImpl?: typeof fetch;
}

export interface ApiResponse {
  id: string;
  expiresAt: string;
}

export interface ApiClient {
  postConsent(snapshot: ConsentSnapshot): Promise<Result<ApiResponse>>;
  putConsent(sessionId: string, snapshot: ConsentSnapshot): Promise<Result<ApiResponse>>;
  getRetryQueue(): RetryQueueItem[];
  flushRetryQueue(): Promise<void>;
}

function buildPayload(brand: string, snapshot: ConsentSnapshot): Record<string, unknown> {
  return {
    sessionId: snapshot.sessionId,
    brand,
    bannerVersion: snapshot.bannerVersion,
    policyVersion: snapshot.policyVersion,
    essential: snapshot.categories.essential,
    analytics: snapshot.categories.analytics,
    marketing: snapshot.categories.marketing,
    consentMethod: snapshot.consentMethod,
  };
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const { apiBase, brand, storage } = options;
  const fetchFn = options.fetchImpl ?? fetch;
  const adapter = createLocalStorageAdapter(storage);
  let inMemoryQueue: RetryQueueItem[] = adapter.loadRetryQueue();

  function persistQueue(): void {
    adapter.saveRetryQueue(inMemoryQueue);
  }

  function enqueue(item: Omit<RetryQueueItem, 'attempt' | 'enqueuedAt'>): void {
    inMemoryQueue.push({ ...item, attempt: 1, enqueuedAt: new Date().toISOString() });
    persistQueue();
  }

  async function doRequest(
    url: string,
    method: 'POST' | 'PUT',
    body: Record<string, unknown>
  ): Promise<Result<ApiResponse>> {
    try {
      const response = await fetchFn(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = `HTTP ${response.status}`;
        return { success: false, error: errorText };
      }

      const data = (await response.json()) as ApiResponse;
      return { success: true, data };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      return { success: false, error: message };
    }
  }

  return {
    async postConsent(snapshot: ConsentSnapshot): Promise<Result<ApiResponse>> {
      const url = `${apiBase}/cookie-consent`;
      const payload = buildPayload(brand, snapshot);
      const result = await doRequest(url, 'POST', payload);

      if (!result.success) {
        enqueue({ snapshot, method: 'POST' });
      }

      return result;
    },

    async putConsent(sessionId: string, snapshot: ConsentSnapshot): Promise<Result<ApiResponse>> {
      const url = `${apiBase}/cookie-consent/${sessionId}`;
      const payload = buildPayload(brand, snapshot);
      const result = await doRequest(url, 'PUT', payload);

      if (!result.success) {
        enqueue({ snapshot, method: 'PUT', sessionId });
      }

      return result;
    },

    getRetryQueue(): RetryQueueItem[] {
      return [...inMemoryQueue];
    },

    async flushRetryQueue(): Promise<void> {
      if (inMemoryQueue.length === 0) return;

      const toProcess = [...inMemoryQueue];
      const remaining: RetryQueueItem[] = [];

      for (const item of toProcess) {
        if (item.attempt > MAX_RETRY_ATTEMPTS) {
          // Abandon after max attempts
          continue;
        }

        const url =
          item.method === 'PUT' && item.sessionId
            ? `${apiBase}/cookie-consent/${item.sessionId}`
            : `${apiBase}/cookie-consent`;

        const payload = buildPayload(brand, item.snapshot);
        const result = await doRequest(url, item.method, payload);

        if (!result.success) {
          remaining.push({ ...item, attempt: item.attempt + 1 });
        }
        // On success: item removed from queue (not added to remaining)
      }

      inMemoryQueue = remaining;
      persistQueue();
    },
  };
}
